import React, { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import LoadingDialog from './LoadingDialog';
import PLYViewer from '../PLYViewer';
import SharedButton from '../SharedButton';
import { reloadPage } from '../../utils/idleTimer';
import { t } from '../../utils/i18n';
import { CERAMIC_SIMILARITIES_QUERY } from '../../app/graphql/ceramic_similarities';

// Similarity Item Component
function SimilarityItem({ ceramic, index, isSelected, onClick }) {
  const similarityPercent = ((ceramic.similarity ?? 0) * 100).toFixed(1);

  const similarityLevel =
    ceramic.similarity >= 0.8 ? 'high' : ceramic.similarity >= 0.6 ? 'medium' : 'low';
  const similarityLabel =
    ceramic.similarity >= 0.8
      ? t('verySimilar')
      : ceramic.similarity >= 0.6
      ? t('moderatelySimilar')
      : t('lessSimilar');

  return (
    <div
      className={`similarity-item ${isSelected ? 'selected' : ''}`}
      onClick={() => onClick(ceramic)}
    >
      {/* Selection indicator bar */}
      {isSelected && <div className="selection-bar" />}

      {/* Top row: Image and info on left, similarity on right */}
      <div className="item-top-row">
        {/* Ceramic Image */}
        <div className="item-image">
          <img src={`/figures/${ceramic.id}/preview`} alt={ceramic.identifier} />
        </div>

        {/* Info - flex to fill space */}
        <div className="item-info">
          <div className="item-identifier">{ceramic.identifier}</div>

          {/* Site and page info - always present, consistent spacing */}
          <div className="item-site">
            {ceramic.site && (
              <>
                <span>📍</span>
                <span>{ceramic.site.name}</span>
              </>
            )}
            {!ceramic.site && <span>&nbsp;</span>}
          </div>

          <div className="item-page">
            <span>
              {t('page')} {ceramic.pageNumber}
            </span>
            <span>
              {t('ceramics')} {ceramic.identifier}
            </span>
          </div>
        </div>

        {/* Right side: Similarity info */}
        <div className="item-right">
          {/* Percentage */}
          <div className={`similarity-percent ${similarityLevel}`}>{similarityPercent}%</div>

          {/* Label badge */}
          <div className={`similarity-badge ${similarityLevel}`}>{similarityLabel}</div>
        </div>
      </div>

      {/* Similarity Progress Bar */}
      <div className="progress-bar">
        <div
          className={`progress-bar-fill ${similarityLevel}`}
          style={{ '--similarity-width': `${similarityPercent}%` }}
        />
      </div>
    </div>
  );
}

export default function SimilarityStep({ figure, goBack, figuresData }) {
  const {
    data,
    error: similarityError,
    loading: loadingSimilarity
  } = useQuery(CERAMIC_SIMILARITIES_QUERY, { variables: { id: figure.id } });
  // The legacy JSON view shape (folded into the ceramicSimilarities query).
  const similarity = data?.ceramicSimilarities;

  const [selectedCeramic, setSelectedCeramic] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Get 3D model URL from figuresData
  const threeDModelUrl = figuresData?.three_d_model || figuresData?.preview_image;

  const handleCeramicClick = (ceramic) => {
    setSelectedCeramic(ceramic);
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  if (loadingSimilarity) {
    return (
      <div className="similarity-empty">
        <LoadingDialog message={t('loading')} />
      </div>
    );
  }

  if (similarityError) {
    return (
      <div className="similarity-error">
        <h3>{t('errorLoadingSimilarities')}</h3>
        <p>{similarityError.message}</p>
        <button className="similarity-back-button" onClick={goBack}>
          {t('back')}
        </button>
      </div>
    );
  }

  if (!similarity) {
    return (
      <div className="similarity-empty">
        <h3>{t('noSimilarityData')}</h3>
        <p>{t('similarityDataNotComputed')}</p>
        <button className="similarity-back-button" onClick={goBack}>
          {t('back')}
        </button>
      </div>
    );
  }

  return (
    <div className="similarity-step">
      {/* Header */}
      <div className="similarity-header">
        <h2>{t('similarityAnalysis')}</h2>
        <p>
          {t('comparingWith')} {similarity?.ceramic.identifier} {t('withOtherVessels')}
        </p>
      </div>

      {/* Statistics Bar */}
      <div className="similarity-controls">
        <SharedButton onClick={goBack} variant="secondary" className="similarity-btn">
          <span>←</span> {t('backToAnalysis')}
        </SharedButton>

        <SharedButton onClick={() => reloadPage()} className="similarity-btn">
          {t('nextAnalysis')}
        </SharedButton>

        <div className="similarity-stats">
          <div className="stat-item">
            <div className="stat-value">{similarity?.totalSimilar}</div>
            <div className="stat-label">{t('similarVessels')}</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{similarity?.publication.title}</div>
            <div className="stat-label">{t('publication')}</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="similarity-main">
        {/* Original Ceramic Panel */}
        <div className="similarity-original">
          <h3>
            {t('originalVessel')}: {similarity?.ceramic.identifier}
          </h3>
          {similarity && (
            <>
              <div className="original-image">
                <img
                  src={`/figures/${similarity.ceramic.id}/preview`}
                  alt={similarity.ceramic.identifier}
                />
              </div>
              <PLYViewer
                modelUrl={threeDModelUrl}
                label={similarity.ceramic.identifier}
                width={400}
                height={400}
              />
            </>
          )}
        </div>

        {/* Similar Ceramics List */}
        <div className="similarity-right">
          <h3>
            {t('similarVessels')}{' '}
            <span className="similarity-count">({similarity?.totalSimilar})</span>
          </h3>
          <div className="similarity-list">
            {(similarity?.similarities ?? []).map((ceramic, index) => (
              <SimilarityItem
                key={ceramic.id}
                ceramic={ceramic}
                index={index}
                isSelected={selectedCeramic?.id === ceramic.id}
                onClick={handleCeramicClick}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && selectedCeramic && (
        <div className="preview-modal" onClick={handleClosePreview}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-preview" onClick={handleClosePreview}>
              ×
            </button>

            <h2>
              {t('detailedComparison')}: {selectedCeramic.identifier}
            </h2>

            <div className="preview-images">
              <div>
                <div className="preview-image-label">{t('selectedCeramic')}</div>
                <div className="preview-image-container">
                  <img
                    src={`/figures/${selectedCeramic.id}/preview`}
                    alt={selectedCeramic.identifier}
                  />
                </div>
              </div>

              <div>
                <div className="preview-image-label">{t('originalCeramic')}</div>
                <div className="preview-image-container">
                  <img
                    src={`/figures/${similarity?.ceramic.id}/preview`}
                    alt={similarity?.ceramic.identifier}
                  />
                </div>
              </div>
            </div>

            {/* Selected Ceramic Details */}
            <div className="preview-details">
              <h4>{t('ceramicDetails')}</h4>
              <table className="detail-table">
                <tbody>
                  <tr>
                    <td>{t('identifier')}</td>
                    <td>{selectedCeramic.identifier}</td>
                  </tr>
                  <tr>
                    <td>{t('similarityScore')}</td>
                    <td>
                      <span
                        className={`similarity-score-value ${
                          selectedCeramic.similarity >= 0.8
                            ? 'high'
                            : selectedCeramic.similarity >= 0.6
                            ? 'medium'
                            : 'low'
                        }`}
                      >
                        {(selectedCeramic.similarity * 100).toFixed(1)}%
                      </span>
                      <span
                        className={`similarity-score-badge ${
                          selectedCeramic.similarity >= 0.8
                            ? 'high'
                            : selectedCeramic.similarity >= 0.6
                            ? 'medium'
                            : 'low'
                        }`}
                      >
                        {selectedCeramic.similarity >= 0.8
                          ? t('verySimilar')
                          : selectedCeramic.similarity >= 0.6
                          ? t('moderatelySimilar')
                          : t('lessSimilar')}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td>{t('page')}</td>
                    <td>
                      {t('page')} {selectedCeramic.pageNumber}
                    </td>
                  </tr>
                  {selectedCeramic.site && (
                    <tr>
                      <td>{t('site')}</td>
                      <td>{selectedCeramic.site.name}</td>
                    </tr>
                  )}
                  <tr>
                    <td>{t('detectionConfidence')}</td>
                    <td>{(selectedCeramic.probability * 100).toFixed(1)}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
